import { gridOf, type Board, type Cell } from '@archon3d/core';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { BOARD_CENTER, BOARD_TOP_Y, CHALLENGE_OFFSET, TILE_SIZE } from './constants';

/** Rows of pointy-top hexes sit √3/2 of a tile apart; odd rows are shifted half a tile east. */
export const HEX_ROW_PITCH = Math.sqrt(3) / 2;
/** Circumradius (and edge) of a hex whose flat-to-flat width is one tile; half its point-to-point height. */
export const HEX_RADIUS = 1 / Math.sqrt(3);

/** Where the cells of a board lie, in tiles, measured from the board's centre on the table. */
interface Layout {
  hex: boolean;
  /** Distance between rows, in tiles. */
  pitch: number;
  /** The board's centre: what `x + rowShift` and `y · pitch` are measured against. */
  middleX: number;
  middleZ: number;
  /** Half the extent of the tiles, east–west and north–south. */
  halfWidth: number;
  halfDepth: number;
}

const layouts = new WeakMap<Board, Layout>();

/** How far row `y` is shifted east, in tiles: half a tile on odd hex rows, none on squares. */
export function rowShift(board: Board, y: number): number {
  if (gridOf(board) !== 'hex') return 0;
  // Averaged positions (a side's home) fall between rows; a quarter tile is the mean shift.
  return Number.isInteger(y) ? (y & 1) * 0.5 : 0.25;
}

/**
 * A square board is centred on its grid. A hex board is centred on its floor cells instead: a
 * hexagon-shaped board carries void corners in its row strings, and it is the hexagon that must
 * sit on the turntable's pivot and under the frame, not the rectangle around it. For a hex
 * board with no void cells the two agree.
 */
export function layoutOf(board: Board): Layout {
  let layout = layouts.get(board);
  if (layout) return layout;
  if (gridOf(board) !== 'hex') {
    layout = {
      hex: false,
      pitch: 1,
      middleX: (board.width - 1) / 2,
      middleZ: (board.height - 1) / 2,
      halfWidth: board.width / 2,
      halfDepth: board.height / 2,
    };
  } else {
    let cells: readonly Cell[] = board.cells.filter((c) => c.kind === 'floor');
    if (cells.length === 0) cells = board.cells;
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const c of cells) {
      const x = c.x + rowShift(board, c.y);
      minX = Math.min(minX, x - 0.5);
      maxX = Math.max(maxX, x + 0.5);
      minY = Math.min(minY, c.y * HEX_ROW_PITCH - HEX_RADIUS);
      maxY = Math.max(maxY, c.y * HEX_ROW_PITCH + HEX_RADIUS);
    }
    layout = {
      hex: true,
      pitch: HEX_ROW_PITCH,
      middleX: (minX + maxX) / 2,
      middleZ: (minY + maxY) / 2,
      halfWidth: (maxX - minX) / 2,
      halfDepth: (maxY - minY) / 2,
    };
  }
  layouts.set(board, layout);
  return layout;
}

/** Size of the playing surface in metres (the tiles' bounding box, before the frame). */
export function boardFootprint(board: Board): { width: number; depth: number } {
  const { halfWidth, halfDepth } = layoutOf(board);
  return { width: 2 * halfWidth * TILE_SIZE, depth: 2 * halfDepth * TILE_SIZE };
}

/** World position of the centre of a cell's top face. */
export function cellToWorld(board: Board, x: number, y: number): Vector3 {
  const { pitch, middleX, middleZ } = layoutOf(board);
  // Babylon is left-handed: seen from the player's side (+Z), world +X is on the left, so cell
  // x = 0 (Light's home column) maps to +X to keep Light on the left as in the original.
  return new Vector3(
    BOARD_CENTER.x - (x + rowShift(board, y) - middleX) * TILE_SIZE,
    BOARD_TOP_Y,
    BOARD_CENTER.z + (y * pitch - middleZ) * TILE_SIZE,
  );
}

/**
 * Where two pieces stand while they contest one square.
 *
 * A capture is fought over a square, not between squares, so the attacker finishes its move on the
 * defender's square rather than stopping short of it, and the defender gives ground rather than
 * being stood inside. Both end up on the square, drawn apart along the line the attacker came in
 * on — the attacker on the near side, the defender pushed back to the far one — so each is a whole
 * piece with room of its own. When the fight is over the survivor has only to take the middle.
 */
export function challengeStances(
  board: Board,
  approach: { x: number; y: number },
  contested: { x: number; y: number },
): { attacker: Vector3; defender: Vector3 } {
  const centre = cellToWorld(board, contested.x, contested.y);
  const along = centre.subtract(cellToWorld(board, approach.x, approach.y));
  along.y = 0;
  const length = along.length();
  // A creature conjured onto the very square it fights for has no line to have come in on.
  if (length < 1e-6) return { attacker: centre.clone(), defender: centre.clone() };
  along.scaleInPlace((CHALLENGE_OFFSET * TILE_SIZE) / length);
  return { attacker: centre.subtract(along), defender: centre.add(along) };
}

/** Outward normals of a flat-top hexagon's sides, one of each opposite pair. */
const HEX_SIDE_NORMALS = [Math.PI / 6, Math.PI / 2, (5 * Math.PI) / 6].map((a) => ({
  x: Math.cos(a),
  z: Math.sin(a),
}));

/**
 * Inradius, in tiles, of the smallest flat-top hexagon centred on the board that holds every
 * floor hex. A pointy-top hex has a corner in each of that hexagon's side directions, so it is
 * the farthest cell centre along each side normal plus the hex's circumradius; for a hexagon of
 * hexes of radius r that comes to r·√3/2 + 1/√3, with the rim's corners exactly on the sides.
 */
export function hexInradius(board: Board): number {
  const { middleX, middleZ } = layoutOf(board);
  let reach = 0;
  for (const c of board.cells) {
    if (c.kind !== 'floor') continue;
    const x = c.x + rowShift(board, c.y) - middleX;
    const z = c.y * HEX_ROW_PITCH - middleZ;
    for (const n of HEX_SIDE_NORMALS) reach = Math.max(reach, Math.abs(x * n.x + z * n.z));
  }
  return reach + HEX_RADIUS;
}
